/**
 * Domain model for the native BRouter map.
 *
 * Mirrors the data shapes of the legacy `brouter-web` client so the URL contract
 * against the BRouter engine stays byte-identical. See `js/router/BRouter.js`.
 */

/** A geographic point. Matches Leaflet's `LatLngLiteral`. */
export interface LatLng {
  lat: number;
  lng: number;
}

/** A user-placed routing waypoint. */
export interface Waypoint extends LatLng {
  /** Stable identity so markers survive reordering. */
  id: string;
  /** Optional label, e.g. the originating station name. */
  name?: string;
}

/** Leaflet viewport restored when the map component is recreated inside a modal. */
export interface MapViewport {
  center: LatLng;
  zoom: number;
}

/** A named point of interest, serialised into the `pois` URL parameter. */
export interface Poi {
  latlng: LatLng;
  name: string;
}

/** Circular no-go area, serialised into the `nogos` URL parameter. */
export interface NogoCircle {
  center: LatLng;
  /** Metres. Rounded to an integer during serialisation. */
  radius: number;
  /** `-1` is the default full exclusion and is omitted from the URL. */
  nogoWeight?: number;
}

/** Open no-go corridor, serialised into the `polylines` URL parameter. */
export interface NogoPolyline {
  vertices: LatLng[];
  nogoWeight?: number;
}

/** Closed no-go region, serialised into the `polygons` URL parameter. */
export interface NogoPolygon {
  vertices: LatLng[];
  nogoWeight?: number;
}

/** Everything that influences a routing request besides the waypoints themselves. */
export interface RoutingOptions {
  profile: string;
  /** BRouter `alternativeidx`, 0-3. */
  alternative: number;
  nogos: NogoCircle[];
  polylines: NogoPolyline[];
  polygons: NogoPolygon[];
}

export const defaultRoutingOptions = (profile: string): RoutingOptions => ({
  profile,
  alternative: 0,
  nogos: [],
  polylines: [],
  polygons: []
});

/** Export file formats offered by the export dialog. */
export type ExportFormat = 'gpx' | 'kml' | 'geojson' | 'csv';

/**
 * Raw BRouter `messages` row. The engine returns a header row of column names
 * followed by one row per track edge.
 */
export type TrackMessage = (string | number)[];

/** Properties block of a BRouter GeoJSON LineString feature. */
export interface BRouterFeatureProperties {
  creator?: string;
  name?: string;
  /** Total distance in metres, as a string. */
  'track-length'?: string;
  /** Cumulative ascent in metres, as a string. */
  'filtered ascend'?: string;
  'plain-ascend'?: string;
  'total-time'?: string;
  'total-energy'?: string;
  cost?: string;
  messages?: TrackMessage[];
  times?: number[];
  voicehints?: number[][];
  [key: string]: unknown;
}

/** A BRouter GeoJSON LineString response, one per routed leg. */
export interface BRouterSegment {
  type: 'Feature';
  geometry: {
    type: 'LineString';
    /** `[lng, lat, elevation?]` triples. */
    coordinates: number[][];
  };
  properties: BRouterFeatureProperties;
}

/** Aggregated figures rendered in the stats footer. */
export interface TrackStats {
  /** Metres. */
  distance: number;
  /** Seconds. */
  totalTime: number;
  /** Kilojoules. */
  totalEnergy: number;
  /** Metres. */
  ascend: number;
  /** Metres. */
  plainAscend: number;
  cost: number;
  meanCostFactor: number;
  /** True when at least one leg is a straight line rather than a routed path. */
  hasBeeline: boolean;
}

export const emptyTrackStats: TrackStats = {
  distance: 0,
  totalTime: 0,
  totalEnergy: 0,
  ascend: 0,
  plainAscend: 0,
  cost: 0,
  meanCostFactor: 0,
  hasBeeline: false
};

/**
 * Payload handed to the backend's `POST /BRouter_interface/track`.
 *
 * The shape is fixed by `tst_input_export_BRouter_track_to_UI.json` in the backend
 * test suite and must not change without updating that fixture.
 */
export interface ExportToUiPayload {
  array_lonlats: LatLng[];
  profile: string;
}
